import { IconAdjustments } from "@tabler/icons-react";
import { useState } from "react";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EditRowActions,
  IconAction,
  Input,
  Switch,
  Td,
  Text,
  Tr,
  useConfirmDialog,
} from "@/components/ui";
import {
  CONFIG_HEREDA,
  ConfiguracionDocumentalFields,
  configuracionValida,
  textoVigencia,
  type ConfiguracionDocumental,
} from "@/features/admin-gestion-documental/configuracion-documental";
import type { TipoDocumentoAdmin } from "../services/tipos-documento-service";
import { useEditarTipoDocumento, useEliminarTipoDocumento } from "../hooks/use-tipo-documento-mutations";

/** Resumen corto de lo que sobrescribe un tipo («hereda todo» si nada). */
export function resumenSobrescrituras(c: ConfiguracionDocumental): string {
  const partes: string[] = [];
  if (c.requiereRevision !== null) partes.push(c.requiereRevision ? "con revisión" : "sin revisión");
  if (c.rolesRevisores && c.rolesRevisores.length > 0) partes.push(`revisa: ${c.rolesRevisores.join(", ")}`);
  if (c.manejaVigencia !== null) partes.push(`vigencia: ${textoVigencia(c).toLowerCase()}`);
  if (c.alVencerEnvio || c.alVencerRevision || c.alVencerAutorizacion) partes.push("vencido: propio");
  return partes.length ? partes.join(" · ") : "Hereda todo de la categoría";
}

/**
 * Fila del catálogo de tipos de documento · alterna vista/edición inline (nombre + activo) y abre
 * el diálogo de configuración (lo que sobrescribe de su categoría). Renombrar/dar de baja NO
 * afecta documentos ya subidos (guardan el nombre como snapshot).
 */
export function TipoDocumentoRow({ tipo }: { tipo: TipoDocumentoAdmin }) {
  const [editando, setEditando] = useState(false);
  const [configurando, setConfigurando] = useState(false);
  const [nombre, setNombre] = useState(tipo.nombre);
  const [activo, setActivo] = useState(tipo.activo);
  const [config, setConfig] = useState<ConfiguracionDocumental>(tipo.sobrescrituras ?? CONFIG_HEREDA);
  const confirm = useConfirmDialog();

  const guardar = useEditarTipoDocumento({ onEditado: () => setEditando(false) });
  const guardarConfig = useEditarTipoDocumento({ onEditado: () => setConfigurando(false) });
  const eliminar = useEliminarTipoDocumento();
  const errorConfig = configuracionValida(config, false);

  const cancelar = () => {
    setNombre(tipo.nombre);
    setActivo(tipo.activo);
    setEditando(false);
  };

  const confirmarEliminar = async () => {
    const ok = await confirm({
      title: "Dar de baja tipo de documento",
      message: (
        <>
          ¿Dar de baja el tipo <b>{tipo.nombre}</b>? Dejará de aparecer al subir documentos. Los documentos ya subidos con
          este tipo NO cambian (guardan el nombre como snapshot). Queda en el catálogo como inactivo y puedes reactivarlo
          editándolo.
        </>
      ),
      confirmLabel: "Dar de baja",
      confirmColor: "peligro",
    });
    if (ok) eliminar.mutate(tipo.id);
  };

  const dialogo = (
    <Dialog open={configurando} onOpenChange={(o) => (!o ? setConfigurando(false) : undefined)}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Configuración de «{tipo.nombre}»</DialogTitle>
          <DialogDescription>Lo que no cambies se hereda de la categoría {tipo.categoriaNombre}.</DialogDescription>
        </DialogHeader>
        <ConfiguracionDocumentalFields value={config} onChange={setConfig} heredable />
        {errorConfig && (
          <Text size="xs" c="peligro-600">
            {errorConfig}
          </Text>
        )}
        <DialogFooter className="mt-4">
          <Button variant="default" onClick={() => setConfig(CONFIG_HEREDA)}>
            Heredar todo
          </Button>
          <Button variant="default" onClick={() => setConfigurando(false)}>
            Cancelar
          </Button>
          <Button
            disabled={!!errorConfig}
            loading={guardarConfig.isPending}
            onClick={() => guardarConfig.mutate({ id: tipo.id, input: { nombre: tipo.nombre, activo: tipo.activo, sobrescrituras: config } })}
          >
            Guardar configuración
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  if (!editando) {
    return (
      <Tr>
        <Td>
          <Text size="sm">{tipo.categoriaNombre}</Text>
        </Td>
        <Td>
          <Text size="sm" fw={500}>
            {tipo.nombre}
          </Text>
          <Text size="xs" c="dimmed">
            {resumenSobrescrituras(tipo.sobrescrituras)}
          </Text>
        </Td>
        <Td>
          <Badge size="sm" color={tipo.activo ? "exito" : "slate"}>
            {tipo.activo ? "Activo" : "Inactivo"}
          </Badge>
        </Td>
        <Td align="right">
          <div className="inline-flex items-center gap-1">
            <IconAction label={`Configuración de ${tipo.nombre}`} variant="subtle" onClick={() => setConfigurando(true)}>
              <IconAdjustments size={16} stroke={1.7} />
            </IconAction>
            <EditRowActions mode="view" onEdit={() => setEditando(true)} onDelete={confirmarEliminar} deleting={eliminar.isPending} />
          </div>
          {dialogo}
        </Td>
      </Tr>
    );
  }

  return (
    <Tr className="bg-muted/50">
      <Td>
        <Text size="sm">{tipo.categoriaNombre}</Text>
      </Td>
      <Td>
        <Input size="xs" value={nombre} onChange={(e) => setNombre(e.currentTarget.value.toUpperCase())} autoFocus />
      </Td>
      <Td>
        <Switch checked={activo} onCheckedChange={setActivo} label={activo ? "Activo" : "Inactivo"} />
      </Td>
      <Td align="right">
        <EditRowActions
          mode="edit"
          saving={guardar.isPending}
          onCancel={cancelar}
          onSave={() => guardar.mutate({ id: tipo.id, input: { nombre: nombre.trim(), activo, sobrescrituras: tipo.sobrescrituras } })}
        />
      </Td>
    </Tr>
  );
}
